// Catches render-time crashes in a view subtree so one broken view shows a
// recoverable panel instead of white-screening the whole app. Mounted in the
// Shell keyed by route, so navigating away clears a crashed view.
import { Component, type ErrorInfo, type ReactNode } from "react";
import { c, font } from "../lib/theme";

interface Props { children: ReactNode; }
interface State { error: Error | null; }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for debugging; in production this is where a
    // telemetry hook (Sentry, etc.) would report the crash.
    console.error("View crashed:", error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: 300, textAlign: "center", padding: 16 }}>
        <div style={{ maxWidth: 440, background: c.panel, border: `1px solid ${c.red}44`, borderRadius: 10, padding: "24px 26px" }}>
          <div style={{ font: `800 24px ${font.display}`, color: c.redHi, marginBottom: 8 }}>This view hit an error</div>
          <div style={{ fontSize: 14.5, color: c.muted2, lineHeight: 1.55, marginBottom: 18 }}>
            Something went wrong rendering this page. Your data is safe — try again, or reload the app.
            <div style={{ font: `500 12.5px ${font.mono}`, color: c.muted, marginTop: 10, wordBreak: "break-word" }}>{error.message}</div>
          </div>
          <button onClick={() => this.setState({ error: null })} style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.onAccent, background: c.blue, border: "none", padding: "9px 18px", borderRadius: 6, cursor: "pointer", marginRight: 8 }}>↻ TRY AGAIN</button>
          <button onClick={() => location.reload()} style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.muted2, background: c.navItem, border: `1px solid ${c.line2}`, padding: "9px 18px", borderRadius: 6, cursor: "pointer" }}>RELOAD APP</button>
        </div>
      </div>
    );
  }
}
